import EventEmitter from 'eventemitter3';
import { ShotStats } from '@/objects/golfBall';
import { type CoursePlayer } from '@/courses/player';

export enum OGSKeyCommands {
  AimLeft = 0,
  AimRight = 1,
  DistanceIncrease = 2,
  DistanceDecrease = 3,
  ClubUp = 4,
  ClubDown = 5,
  PlayerUp = 6,
  PlayerDown = 7,
  Drop = 8,
  ReHit = 9,
  Mulligan = 10,
  Scorecard = 11,
  ToggleMap = 12
};

export type SetupMessage = {
  type: 'setup',
  setupData: OpenGolfSim.SetupData,
  gameData: OpenGolfSim.GameData,
};

export type ShotMessage = {
  type: 'shot',
  shot: OpenGolfSim.Shot
};

/**
 * The launch monitor's software has changed club.
 *
 * FUSE does not choose the club when a monitor is driving — the player picks it
 * in OpenFlight (or whatever connector is in front of the monitor), and the two
 * disagreeing is worse than FUSE not showing one at all. `code` is the
 * connector's own identifier where it sends one ('7I', 'DR', 'PW'), `name` a
 * label to display; either may be absent.
 */
export type ClubMessage = {
  type: 'club',
  code?: string,
  name?: string,
};

export type CommandMessage = {
  type: 'command',
  key: {
    ogs_code?: OGSKeyCommands;
    name: string;
    code: number;
  },
  state: 'down' | 'up' | 'press'
};

export type ReadyMessage = {
  type: 'ready'
};

interface EventMap {
  ready: () => void;
  command: (key: CommandMessage['key'], state: CommandMessage['state']) => void;
  shot: (shotData: OpenGolfSim.Shot) => void;
  setup: (message: Omit<SetupMessage, 'type'>) => void;
  bridgeStatus: (connected: boolean) => void;
  /** The connector switched club — see ClubMessage. */
  club: (club: Omit<ClubMessage, 'type'>) => void;
}

/**
 * Sets up physics and communication with external apps.
 */
export class AppBridge extends EventEmitter<EventMap> {
  appType: 'mobile' | 'desktop' | 'web' | 'webapp';
  isReady: boolean;

  constructor() {
    super();
    this.isReady = false;
    this.appType = 'web';
    if (typeof window.ReactNativeWebView !== 'undefined') {
      this.appType = 'mobile';
    } else if (typeof window.ogsElectron !== 'undefined') {
       this.appType = 'desktop';
    } else if (window.self !== window.top) {
      this.appType = 'webapp';
    }
    if (this.appType === 'desktop') {
      window.ogsElectron!.onMessage(this.#handleElectronMessage.bind(this));
    } else {
      window.addEventListener("message", this.#handleWindowMessage.bind(this));
    }
    this.setReady();
  }

  #handleWindowMessage(event: MessageEvent<any>) {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      this.#handleEvent(data);
    } catch (error) {
      console.log('Could not parse ReactNative message', error);
      console.log(event);
    }
  }

  #handleElectronMessage(data: any) {
    this.#handleEvent(data);
  }

  #handleEvent(data: CommandMessage | ShotMessage | SetupMessage | ClubMessage) {
    switch (data.type) {
      case 'shot':
        this.emit('shot', data.shot);
        break;
      case 'club':
        this.emit('club', { code: data.code, name: data.name });
        break;
      case 'setup':
        this.emit('setup', data);
        break;
      case 'command':
        this.emit('command', data.key, data.state);
        break;
    }
  }

  #ws?: WebSocket;
  #wsUrl?: string;
  #wsRetryMs = 1000;
  #wsClosedByUser = false;
  #wsReconnectTimer?: ReturnType<typeof setTimeout>;

  /**
   * Connect to an MR shot bridge (FUSE Link on the headset, or the dev relay
   * on a LAN PC). Messages use the same `{type:'shot', shot}` shape as
   * postMessage, so they route through the existing event handling and
   * downstream consumers are unchanged. Reconnects with capped backoff.
   */
  connectWebSocket(url: string) {
    this.disconnectWebSocket();
    this.#wsClosedByUser = false;
    this.#wsUrl = url;
    this.#openWebSocket();
  }

  disconnectWebSocket() {
    this.#wsClosedByUser = true;
    if (this.#wsReconnectTimer) {
      clearTimeout(this.#wsReconnectTimer);
      this.#wsReconnectTimer = undefined;
    }
    // Detach handlers and null the field BEFORE close(): the old socket's
    // late onclose must never emit status or spawn a reconnect
    const ws = this.#ws;
    this.#ws = undefined;
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      ws.close();
    }
  }

  #openWebSocket() {
    if (!this.#wsUrl || this.#ws) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.#wsUrl);
    } catch (error) {
      console.warn('Bridge connection failed', error);
      this.#scheduleWsReconnect();
      return;
    }
    this.#ws = ws;
    // Every handler checks it still owns the slot — a replaced socket must
    // not deliver messages (duplicate shots) or flap bridgeStatus
    ws.onopen = () => {
      if (this.#ws !== ws) return;
      this.#wsRetryMs = 1000;
      console.log(`[bridge] connected: ${this.#wsUrl}`);
      this.emit('bridgeStatus', true);
    };
    ws.onmessage = (event: MessageEvent<any>) => {
      if (this.#ws !== ws) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        this.#handleEvent(data);
      } catch (error) {
        console.log('Could not parse bridge message', error);
      }
    };
    ws.onclose = () => {
      if (this.#ws !== ws) return;
      this.#ws = undefined;
      this.emit('bridgeStatus', false);
      this.#scheduleWsReconnect();
    };
    ws.onerror = () => {
      // onclose follows and handles reconnect
    };
  }

  #scheduleWsReconnect() {
    if (this.#wsClosedByUser || this.#wsReconnectTimer || this.#ws) return;
    this.#wsReconnectTimer = setTimeout(() => {
      this.#wsReconnectTimer = undefined;
      this.#openWebSocket();
    }, this.#wsRetryMs);
    this.#wsRetryMs = Math.min(this.#wsRetryMs * 2, 10000);
  }

  initialize(callback: () => void) {
    if (this.isReady) {
      return callback();
    }
    this.once('ready', callback);
  }
  
  log(message: any) {
    this.sendMessage({ type: 'log', message });
  }

  setReady() {
    console.log('[runtime] FUSE initialized');
    this.isReady = true;
    this.sendMessage({ type: 'ready' });
  }

  sendPlayerUpdate(player: CoursePlayer, position: [number, number, number]) {
    const { name, id, clubs } = player;
    const update: OpenGolfSim.PlayerUpdateEvent = {
      type: 'player',
      player: { name, id, clubs },
      currentPosition: position,
      club: player.currentClub
    };
    this.sendMessage(update);
  }

  sendShotResult(options: {
    shot?: OpenGolfSim.Shot,
    stats?: ShotStats,
    player?: OpenGolfSim.Player,
    club?: OpenGolfSim.Club
  }) {
    const { shot, stats, player, club } = options;
    if (!shot) throw new Error('Missing shot data');
    if (!stats) throw new Error('Missing result stats');
    if (!player) throw new Error('Missing player');
    if (!club) throw new Error('Missing club');

    const {
      startPosition,
      landPosition,
      endPosition,
      lateralSamples,
      heightSamples,
      distanceSamples,
      surface,
      apex,
      lateral,
      carry,
      total,
      roll
    } = stats || {};

    const update: OpenGolfSim.ShotResultEvent = {
      type: 'result',
      data: { apex, lateral, carry, total, roll },
      shot,
      player,
      club,
      surface,
      startPosition: startPosition?.toArray(),
      endPosition: endPosition?.toArray(),
      landPosition: landPosition?.toArray(),
      lateralSamples: [],
      heightSamples: [],
      distanceSamples: [],
      // lateralSamples,
      // heightSamples,
      // distanceSamples,
    }
    this.sendMessage(update);
  }
  exit() {

    this.sendMessage({ type: 'exit' });

    if (this.appType === 'web') {
      try {
        window.navigation.back();
      } catch (error) {
        console.log(error);
      }
    }
  }

  sendMessage(payload: any) {
    console.log('send', this.appType, payload);

    // Mirror outbound traffic to a connected bridge (FUSE Link / mr-bridge):
    // shot results, player updates and ready flow back to whatever is
    // driving the launch monitor
    let sentToBridge = false;
    if (this.#ws?.readyState === WebSocket.OPEN) {
      try {
        this.#ws.send(JSON.stringify(payload));
        sentToBridge = true;
      } catch (error) {
        console.warn('Could not send to bridge', error);
      }
    }

    if (this.appType === 'mobile') {
      console.log('Sending to react native: ', payload);
      window.ReactNativeWebView?.postMessage(JSON.stringify(payload));
    } else if (this.appType === 'desktop') {
      console.log('Sending to electron: ', payload);
      window.ogsElectron?.postMessage(payload);
    } else if (this.appType === 'webapp' && window.parent?.postMessage) {
      console.log('Sending to iframe: ', payload);
      window.parent.postMessage(payload, '*');
    } else if (payload.type === 'ready') {
      this.emit('ready');
    } else if (!sentToBridge) {
      console.warn('No parent to send message to!', payload);
    }
  }
}

